// src/email/components/ThreadList.jsx
import React, { useMemo, useState } from 'react';
import {
  useTrashThread,
  useUntrashThread,
  useMarkThreadRead,
  useMarkThreadUnread,
  useDeleteThreadForever,
} from '../api/hooks';

const AVATAR_PALETTES = [
  { bg: '#eff6ff', color: '#1d4ed8' }, // blue
  { bg: '#f5f3ff', color: '#6d28d9' }, // purple
  { bg: '#fdf2f8', color: '#be185d' }, // pink
  { bg: '#ecfdf5', color: '#047857' }, // emerald
  { bg: '#fffbeb', color: '#b45309' }, // amber
  { bg: '#eef2ff', color: '#4338ca' }, // indigo
  { bg: '#ecfeff', color: '#0e7490' }, // cyan
  { bg: '#fff7ed', color: '#c2410c' }, // orange
];

function getAvatarPalette(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const index = Math.abs(hash) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[index];
}

function cleanSenderName(raw) {
  if (!raw) return 'Unknown';
  const match = raw.match(/^"?([^"<]+)"?\s*(?:<.*>)?$/);
  if (match && match[1]?.trim()) {
    return match[1].trim();
  }
  if (raw.includes('@')) {
    return raw.split('@')[0].replace(/[._-]/g, ' ');
  }
  return raw;
}

function getInitial(raw) {
  const clean = cleanSenderName(raw).trim();
  return clean ? clean.charAt(0).toUpperCase() : '?';
}

function cleanSnippet(raw) {
  if (!raw) return '';
  return raw
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return sameYear
    ? d.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : d.toLocaleDateString([], { month: 'numeric', day: 'numeric', year: '2-digit' });
}

// Where a search result lives, derived from its Gmail labels
function folderLabel(labelIds) {
  const labels = labelIds || [];
  if (labels.includes('TRASH')) return 'Trash';
  if (labels.includes('SPAM')) return 'Spam';
  if (labels.includes('DRAFT')) return 'Draft';
  if (labels.includes('SENT') && !labels.includes('INBOX')) return 'Sent';
  return 'Inbox';
}

// Free-text terms of a query (operator:value and -excluded terms are skipped)
function highlightTerms(query) {
  const terms = [];
  for (const m of query.matchAll(/(-?)(?:([a-z_]+):)?(?:"([^"]*)"|(\S+))/gi)) {
    const [, dash, operator, quoted, bare] = m;
    if (!dash && !operator) terms.push(quoted ?? bare);
  }
  return terms.filter(Boolean);
}

function renderHighlighted(text, regex) {
  if (!regex || !text) return text;
  return text.split(regex).map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="search-hit">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export function ThreadList({
  threads = [],
  activeThreadId,
  onSelect,
  isLoading,
  isFetching,
  activeFolderName = 'Inbox',
  pageIndex = 0,
  pageSize = 50,
  totalCount,
  hasMore = false,
  onNextPage,
  onPrevPage,
  searchQuery = '',
  isSearchError = false,
  isSearchStale = false,
  onRetrySearch,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [rowActionMap, setRowActionMap] = useState({});
  const [bulkLoading, setBulkLoading] = useState(null);

  const isSearch = Boolean(searchQuery);
  const hitRegex = useMemo(() => {
    const terms = highlightTerms(searchQuery)
      .sort((a, b) => b.length - a.length)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return terms.length > 0 ? new RegExp(`(${terms.join('|')})`, 'gi') : null;
  }, [searchQuery]);

  const searchHeader = isSearch ? (
    <div className="search-results-header">Search results for "{searchQuery}"</div>
  ) : null;

  const trashThread = useTrashThread();
  const untrashThread = useUntrashThread();
  const markThreadRead = useMarkThreadRead();
  const markThreadUnread = useMarkThreadUnread();
  const deleteThreadForever = useDeleteThreadForever();

  const isTrashFolder = !isSearch && activeFolderName?.toLowerCase() === 'trash';

  const allSelected = threads.length > 0 && selectedIds.size === threads.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(threads.map((t) => t.id)));
    }
  };

  const toggleSelectOne = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRowRestore = async (threadId, e) => {
    e.stopPropagation();
    setRowActionMap((prev) => ({ ...prev, [threadId]: 'restore' }));
    try {
      await untrashThread.mutateAsync(threadId);
    } catch (err) {
      console.error('Failed to restore thread', err);
    } finally {
      setRowActionMap((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });
    }
  };

  const handleRowTrash = async (threadId, e, inTrash = isTrashFolder) => {
    e.stopPropagation();
    setRowActionMap((prev) => ({ ...prev, [threadId]: 'trash' }));
    try {
      if (inTrash) {
        await deleteThreadForever.mutateAsync(threadId);
      } else {
        await trashThread.mutateAsync(threadId);
      }
    } catch (err) {
      console.error('Failed to trash thread', err);
    } finally {
      setRowActionMap((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });
    }
  };

  const handleRowMarkReadToggle = async (threadId, isUnread, e) => {
    e.stopPropagation();
    const actionKey = isUnread ? 'read' : 'unread';
    setRowActionMap((prev) => ({ ...prev, [threadId]: actionKey }));
    try {
      if (isUnread) {
        await markThreadRead.mutateAsync(threadId);
      } else {
        await markThreadUnread.mutateAsync(threadId);
      }
    } catch (err) {
      console.error('Failed to toggle read state', err);
    } finally {
      setRowActionMap((prev) => {
        const next = { ...prev };
        delete next[threadId];
        return next;
      });
    }
  };

  const handleBulkMarkRead = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading('read');
    const ids = Array.from(selectedIds);
    setRowActionMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = 'read'));
      return next;
    });
    try {
      await Promise.all(ids.map((id) => markThreadRead.mutateAsync(id)));
    } catch (err) {
      console.error('Bulk mark read failed', err);
    } finally {
      setBulkLoading(null);
      setSelectedIds(new Set());
      setRowActionMap((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id] === 'read') delete next[id];
        });
        return next;
      });
    }
  };

  const handleBulkMarkUnread = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading('unread');
    const ids = Array.from(selectedIds);
    setRowActionMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = 'unread'));
      return next;
    });
    try {
      await Promise.all(ids.map((id) => markThreadUnread.mutateAsync(id)));
    } catch (err) {
      console.error('Bulk mark unread failed', err);
    } finally {
      setBulkLoading(null);
      setSelectedIds(new Set());
      setRowActionMap((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id] === 'unread') delete next[id];
        });
        return next;
      });
    }
  };

  const handleBulkTrash = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading('trash');
    const ids = Array.from(selectedIds);
    setRowActionMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = 'trash'));
      return next;
    });
    try {
      if (isTrashFolder) {
        await Promise.all(ids.map((id) => deleteThreadForever.mutateAsync(id)));
      } else {
        await Promise.all(ids.map((id) => trashThread.mutateAsync(id)));
      }
    } catch (err) {
      console.error('Bulk trash failed', err);
    } finally {
      setBulkLoading(null);
      setSelectedIds(new Set());
      setRowActionMap((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id] === 'trash') delete next[id];
        });
        return next;
      });
    }
  };

  const handleBulkRestore = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading('restore');
    const ids = Array.from(selectedIds);
    setRowActionMap((prev) => {
      const next = { ...prev };
      ids.forEach((id) => (next[id] = 'restore'));
      return next;
    });
    try {
      await Promise.all(ids.map((id) => untrashThread.mutateAsync(id)));
    } catch (err) {
      console.error('Bulk restore failed', err);
    } finally {
      setBulkLoading(null);
      setSelectedIds(new Set());
      setRowActionMap((prev) => {
        const next = { ...prev };
        ids.forEach((id) => {
          if (next[id] === 'restore') delete next[id];
        });
        return next;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="modern-list-wrapper">
        {searchHeader}
        <div className="thread-list-empty">
          <div className="loading-spinner"></div>
          <span>{isSearch ? 'Searching…' : 'Loading conversations…'}</span>
        </div>
      </div>
    );
  }

  if (isSearch && isSearchError) {
    return (
      <div className="modern-list-wrapper">
        {searchHeader}
        <div className="thread-list-empty">
          <h3>Search failed. Try again.</h3>
          <button type="button" className="btn btn-secondary" onClick={onRetrySearch}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isSearch && threads.length === 0) {
    return (
      <div className="modern-list-wrapper">
        {searchHeader}
        <div className="thread-list-empty">
          <h3>No conversations matched "{searchQuery}"</h3>
          <p>
            Search looks in all mail except Spam and Trash. Add <code>in:anywhere</code> to include them.
          </p>
        </div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className="modern-list-wrapper">
        <div className="thread-list-empty">
          <div className="empty-illustration">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
              <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
              <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
          </div>
          <h3>Your {activeFolderName} is empty</h3>
          <p>No messages here right now. Any new incoming emails will appear automatically.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-list-wrapper">
      {searchHeader}
      {/* Clean Single Gmail-Style Toolbar */}
      <div className={`list-action-toolbar ${selectedIds.size > 0 ? 'is-bulk-active' : ''}`}>
        <div className="toolbar-left">
          <label className="checkbox-wrap" title="Select all conversations">
            <input
              type="checkbox"
              className="modern-checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={toggleSelectAll}
            />
            <span className="checkbox-label-text">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </span>
          </label>

          {selectedIds.size > 0 && (
            <div className="bulk-action-buttons">
              {isTrashFolder ? (
                <button
                  type="button"
                  className="bulk-btn restore"
                  onClick={handleBulkRestore}
                  disabled={bulkLoading !== null}
                  title="Restore selected to Inbox"
                >
                  {bulkLoading === 'restore' ? (
                    <span className="mini-spinner-green" />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 14 4 9 9 4" />
                      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                    </svg>
                  )}
                  <span>{bulkLoading === 'restore' ? 'Restoring selected…' : 'Restore to Inbox'}</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="bulk-btn"
                    onClick={handleBulkMarkRead}
                    disabled={bulkLoading !== null}
                    title="Mark selected as read"
                  >
                    {bulkLoading === 'read' ? (
                      <span className="mini-spinner" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                      </svg>
                    )}
                    <span>{bulkLoading === 'read' ? 'Marking read…' : 'Mark as read'}</span>
                  </button>
                  <button
                    type="button"
                    className="bulk-btn"
                    onClick={handleBulkMarkUnread}
                    disabled={bulkLoading !== null}
                    title="Mark selected as unread"
                  >
                    {bulkLoading === 'unread' ? (
                      <span className="mini-spinner" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v8.25A2.25 2.25 0 0 0 4.5 19.5h15a2.25 2.25 0 0 0 2.25-2.25V13.5" />
                        <path d="M21.75 6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                      </svg>
                    )}
                    <span>{bulkLoading === 'unread' ? 'Marking unread…' : 'Mark as unread'}</span>
                  </button>
                  <button
                    type="button"
                    className="bulk-btn danger"
                    onClick={handleBulkTrash}
                    disabled={bulkLoading !== null}
                    title="Move selected to trash"
                  >
                    {bulkLoading === 'trash' ? (
                      <span className="mini-spinner-danger" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    )}
                    <span>{bulkLoading === 'trash' ? 'Deleting…' : 'Delete'}</span>
                  </button>
                </>
              )}
              <button
                type="button"
                className="bulk-btn cancel"
                onClick={() => setSelectedIds(new Set())}
                disabled={bulkLoading !== null}
                title="Clear selection"
              >
                <span>Deselect</span>
              </button>
            </div>
          )}
        </div>

        <div className="toolbar-right">
          <span className="pagination-label">
            {isSearch
              ? `${threads.length}${hasMore ? '+' : ''} ${threads.length === 1 && !hasMore ? 'result' : 'results'}`
              : threads.length > 0
              ? `${pageIndex * pageSize + 1}–${pageIndex * pageSize + threads.length}${
                  totalCount !== null && totalCount !== undefined ? ` of ${totalCount}` : ''
                }`
              : '0 of 0'}
          </span>
          <div className="pagination-actions" style={isSearch ? { display: 'none' } : undefined}>
            <button
              type="button"
              className="pagination-nav-btn"
              disabled={pageIndex === 0 || isLoading || isFetching}
              onClick={onPrevPage}
              title="Newer"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="pagination-nav-btn"
              disabled={!hasMore || isLoading || isFetching}
              onClick={onNextPage}
              title="Older"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Table Rows Container */}
      <div
        className={`table-scroll-area ${isSearchStale ? 'is-search-stale' : ''}`}
        onScroll={(e) => {
          // Search results page in on scroll-to-bottom
          if (!isSearch || !hasMore || isFetching) return;
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) onNextPage?.();
        }}
      >
        {threads.map((t) => {
          const rowInTrash = isSearch ? Boolean(t.labelIds?.includes('TRASH')) : isTrashFolder;
          const rawSender = t.participants?.[0] || '';
          const sender = cleanSenderName(rawSender);
          const initial = getInitial(rawSender);
          const palette = getAvatarPalette(sender);
          const snippet = cleanSnippet(t.snippet);
          const isSelected = selectedIds.has(t.id);
          const isUnread = Boolean(t.isUnread);
          const hasAttachment = Boolean(
            t.hasAttachments ||
              t.messages?.some((m) => m.attachments && m.attachments.length > 0)
          );
          const targetMessageId = t.lastMessageId || t.messages?.[t.messages.length - 1]?.id;

          const isRowAction = rowActionMap[t.id];
          const isRowRestoring =
            isRowAction === 'restore' ||
            (untrashThread.isPending && untrashThread.variables === t.id);
          const isRowTrashing =
            isRowAction === 'trash' ||
            (trashThread.isPending && trashThread.variables === t.id) ||
            (deleteThreadForever.isPending && deleteThreadForever.variables === t.id);
          const isRowMarkingRead =
            isRowAction === 'read' ||
            isRowAction === 'unread' ||
            (markThreadRead.isPending && markThreadRead.variables === t.id) ||
            (markThreadUnread.isPending && markThreadUnread.variables === t.id);
          const isRowMutating = Boolean(isRowRestoring || isRowTrashing || isRowMarkingRead);

          return (
            <div
              key={t.id}
              className={`email-row ${isUnread ? 'is-unread' : 'is-read'} ${isSelected ? 'is-selected' : ''} ${isRowMutating ? 'is-row-mutating' : ''}`}
              onClick={() => onSelect(t.id)}
              role="row"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSelect(t.id);
              }}
            >
              {/* Left Unread Accent Indicator */}
              {isUnread && <div className="row-unread-accent" />}

              {/* Checkbox */}
              <div className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="modern-checkbox"
                  checked={isSelected}
                  onChange={(e) => toggleSelectOne(t.id, e)}
                />
              </div>

              {/* Sender Avatar */}
              <div
                className="col-avatar"
                style={{ backgroundColor: palette.bg, color: palette.color }}
                title={sender}
              >
                {initial}
              </div>

              {/* Sender Name */}
              <div className="col-sender" title={rawSender}>
                <span className="sender-name">{sender}</span>
              </div>

              {/* Subject & Snippet Inline */}
              <div className="col-subject-snippet">
                <span className="subject-title">
                  {renderHighlighted(t.subject || '(no subject)', hitRegex)}
                </span>
                {snippet && (
                  <>
                    <span className="snippet-dash">—</span>
                    <span className="snippet-preview">{renderHighlighted(snippet, hitRegex)}</span>
                  </>
                )}
              </div>

              {isSearch && <span className="col-folder-chip">{folderLabel(t.labelIds)}</span>}

              {/* Attachment Badge */}
              {hasAttachment && (
                <div className="col-attachment" title="Attachment attached">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </div>
              )}

              {/* Quick Action Buttons */}
              {rowInTrash ? (
                <div className="col-restore-direct" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className={`btn-row-restore ${isRowRestoring ? 'is-loading' : ''}`}
                    title="Restore to Inbox"
                    disabled={isRowRestoring}
                    onClick={(e) => handleRowRestore(t.id, e)}
                  >
                    {isRowRestoring ? (
                      <>
                        <span className="mini-spinner-green" />
                        <span>Restoring…</span>
                      </>
                    ) : (
                      <>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 14 4 9 9 4" />
                          <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                        </svg>
                        <span>Restore Now</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                <div className="col-hover-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="hover-action-icon"
                    title={isUnread ? 'Mark as read' : 'Mark as unread'}
                    disabled={isRowMarkingRead}
                    onClick={(e) => handleRowMarkReadToggle(t.id, isUnread, e)}
                  >
                    {isRowMarkingRead ? (
                      <span className="mini-spinner" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {isUnread ? (
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        ) : (
                          <path d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2m20 0l-10 7L2 6" />
                        )}
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    className="hover-action-icon is-danger"
                    title="Move to trash"
                    disabled={isRowTrashing}
                    onClick={(e) => handleRowTrash(t.id, e, rowInTrash)}
                  >
                    {isRowTrashing ? (
                      <span className="mini-spinner-danger" />
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    )}
                  </button>
                </div>
              )}

              {/* Date */}
              <div className="col-date">
                {formatDate(t.lastMessageAt)}
              </div>
            </div>
          );
        })}

        {/* Bottom Pagination Bar */}
        {hasMore && (
          <div className="thread-list-bottom-bar">
            <button
              type="button"
              className="btn-load-more"
              disabled={isLoading || isFetching}
              onClick={onNextPage}
            >
              {isFetching ? (
                <>
                  <span className="mini-spinner"></span>
                  <span>{isSearch ? 'Loading more results…' : 'Loading next 50 conversations…'}</span>
                </>
              ) : (
                <>
                  <span>{isSearch ? 'Load more results' : 'Load next 50 conversations'}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
